import React, { createContext, useContext, useState } from "react";
import {
  AuthorizedProgressContextState,
  useServiceProgressContext,
} from "@/hooks/progress-context/use-service-progress-provider";
import { useGeneratedSongUrl } from "@/hooks/use-generated-song-url";
import {
  forceCancelVocalRemove,
  getActiveVocalRemoveById,
} from "@/actions/db/vocal-removes";

interface VocalRemoverProgressContextState
  extends AuthorizedProgressContextState {
  vocalFileUrl: string;
  instrumentalFileUrl: string;
}

const VocalRemoverProgressContext = createContext<
  VocalRemoverProgressContextState | undefined
>(undefined);

export const useVocalRemoverProgressContext =
  (): VocalRemoverProgressContextState => {
    const context = useContext(VocalRemoverProgressContext);
    if (!context) {
      throw new Error(
        "useVocalRemoverProgressContext must be used within an VocalRemoverProgressProvider",
      );
    }
    return context;
  };

export const VocalRemoverProgressProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [vocalFileUrl, setVocalFileUrl] = useState<string>("");
  const [instrumentalFileUrl, setInstrumentalFileUrl] = useState<string>("");

  const { getFileUrlByName } = useGeneratedSongUrl("/api/vocal-remove/get?");
  const handleCompleteMessage = async (message: any) => {
    const savedSongs = message.songs;

    const vocalFileUrl = await getFileUrlByName(savedSongs["vocal"]);
    const instrumentalFileUrl = await getFileUrlByName(
      savedSongs["instrumental"],
    );

    setVocalFileUrl(vocalFileUrl ?? "");
    setInstrumentalFileUrl(instrumentalFileUrl ?? "");
  };

  const baseContext = useServiceProgressContext({
    forceCancelProcess: forceCancelVocalRemove,
    getActiveItemByUserId: getActiveVocalRemoveById,
    handleCompleteMessage,
    taskType: "vocalRemove",
  });

  return (
    <VocalRemoverProgressContext.Provider
      value={{ ...baseContext, vocalFileUrl, instrumentalFileUrl }}
    >
      {children}
    </VocalRemoverProgressContext.Provider>
  );
};
